// Map a library_items row to a CSL-JSON object (the format Zotero / Pandoc / etc. consume)
// and produce a short inline citation. Full APA/Chicago/MLA rendering is intentionally not
// done here — the goal is to store data in a citation-ready shape so a future rendering
// layer can plug in without re-engineering anything.
import type { LibraryItem } from "@/lib/library";

export type CslItem = {
  id: string;
  type: string;
  title?: string;
  "container-title"?: string;
  publisher?: string;
  issued?: { "date-parts": number[][] };
  author?: Array<{ literal?: string; family?: string; given?: string }>;
  ISBN?: string;
  DOI?: string;
  URL?: string;
  language?: string;
  volume?: string;
  edition?: string;
  accessed?: { "date-parts": number[][] };
};

const CSL_TYPE_BY_SOURCE: Record<NonNullable<LibraryItem["source_type"]>, string> = {
  book: "book",
  journal_article: "article-journal",
  chapter: "chapter",
  proceedings_article: "paper-conference",
  website: "webpage",
  report: "report",
  podcast: "broadcast",
  other: "document",
};

const splitAuthors = (item: LibraryItem) => {
  const fromArray = Array.isArray(item.authors)
    ? item.authors.filter((value): value is string => typeof value === "string")
    : [];
  if (fromArray.length) return fromArray;
  if (item.author_text) {
    return item.author_text.split(",").map((value) => value.trim()).filter(Boolean);
  }
  return [];
};

const toAuthorParts = (name: string) => {
  const trimmed = name.trim();
  if (!trimmed) return null;
  const lastSpace = trimmed.lastIndexOf(" ");
  if (lastSpace === -1) return { literal: trimmed };
  return { given: trimmed.slice(0, lastSpace).trim(), family: trimmed.slice(lastSpace + 1).trim() };
};

const yearOf = (item: LibraryItem) => {
  if (item.published_year) return item.published_year;
  const match = item.published_date?.match(/\b(\d{4})\b/);
  return match ? Number.parseInt(match[1], 10) : null;
};

export const toCslJson = (item: LibraryItem): CslItem => {
  const year = yearOf(item);
  const csl: CslItem = {
    id: item.id,
    type: CSL_TYPE_BY_SOURCE[item.source_type] ?? "document",
    title: item.display_title || item.title,
  };
  const authors = splitAuthors(item).map(toAuthorParts).filter(Boolean) as CslItem["author"];
  if (authors?.length) csl.author = authors;
  if (item.container_title) csl["container-title"] = item.container_title;
  if (item.publisher) csl.publisher = item.publisher;
  if (year) csl.issued = { "date-parts": [[year]] };
  if (item.isbn_13 || item.isbn_10) csl.ISBN = item.isbn_13 ?? item.isbn_10 ?? undefined;
  if (item.doi) csl.DOI = item.doi;
  if (item.url) csl.URL = item.url;
  if (item.language_code) csl.language = item.language_code;
  if (item.volume) csl.volume = item.volume;
  if (item.edition) csl.edition = item.edition;
  if (item.accessed_date) {
    const [y, m, d] = item.accessed_date.split("-").map((v) => Number.parseInt(v, 10));
    if (y) csl.accessed = { "date-parts": [[y, m || 1, d || 1]] };
  }
  return csl;
};

// Format seconds as M:SS or H:MM:SS. Mirrors formatTimestamp in excerptDropFormat.
const formatTimestampShort = (seconds: number | null | undefined): string | null => {
  if (seconds == null || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
};

export type ShortCiteOptions = { page?: number | null; timestampSeconds?: number | null };

// Short inline preview, e.g. "Smith 2019, p. 42" or "Plato 2003".
// For podcasts with a container_title: `Last — Show Name, "Episode" (Year) 12:34`.
// Backwards-compatible: the second positional arg may be a page number (legacy)
// OR an options object with { page, timestampSeconds }.
export const shortCite = (
  item: LibraryItem,
  pageOrOptions?: number | null | ShortCiteOptions,
): string => {
  const opts: ShortCiteOptions =
    pageOrOptions != null && typeof pageOrOptions === "object"
      ? pageOrOptions
      : { page: (pageOrOptions ?? null) as number | null };
  const page = opts.page ?? null;
  const timestamp = formatTimestampShort(opts.timestampSeconds ?? null);

  const authors = splitAuthors(item);
  const lead = (() => {
    if (!authors.length) return item.display_title || item.title;
    const first = authors[0];
    const last = first.includes(" ") ? first.slice(first.lastIndexOf(" ") + 1) : first;
    if (authors.length === 1) return last;
    if (authors.length === 2) {
      const second = authors[1];
      const secondLast = second.includes(" ") ? second.slice(second.lastIndexOf(" ") + 1) : second;
      return `${last} & ${secondLast}`;
    }
    return `${last} et al.`;
  })();
  const year = yearOf(item);

  // Podcast variant — surfaces the show (container_title) as the work-of-record.
  if (item.source_type === "podcast" && item.container_title) {
    const episodeTitle = item.display_title || item.title;
    const yearPart = year ? ` (${year})` : "";
    const stampPart = timestamp ? ` ${timestamp}` : "";
    const accessedPart = item.accessed_date ? `, accessed ${item.accessed_date}` : "";
    const leadPart = authors.length ? `${lead} \u2014 ` : "";
    return `${leadPart}${item.container_title}, \u201C${episodeTitle}\u201D${yearPart}${stampPart}${accessedPart}`;
  }

  const yearPart = year ? ` ${year}` : "";
  const pagePart = page ? `, p. ${page}` : "";
  return `${lead}${yearPart}${pagePart}`;
};

